import React, { useRef, useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Horizontal testimonial strip: arrows, vertical wheel → horizontal scroll,
 * thin scrollbar. Use inside a min-w-0 parent when in flex layouts.
 */
export function TestimonialScrollRow({ className, children, ...props }) {
  const ref = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const updateEdges = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    if (max <= 1) {
      setCanPrev(false);
      setCanNext(false);
      return;
    }
    setCanPrev(el.scrollLeft > 2);
    setCanNext(el.scrollLeft < max - 2);
  }, []);

  const scrollStep = useCallback(() => {
    const el = ref.current;
    const first = el?.firstElementChild;
    if (!first) return 320;
    const gap = 20; /* gap-5 */
    return first.getBoundingClientRect().width + gap;
  }, []);

  const scrollPrev = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: -scrollStep(), behavior: "smooth" });
  }, [scrollStep]);

  const scrollNext = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: scrollStep(), behavior: "smooth" });
  }, [scrollStep]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onWheel = (e) => {
      if (el.scrollWidth <= el.clientWidth + 1) return;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;

      const maxScroll = el.scrollWidth - el.clientWidth;
      const atLeft = el.scrollLeft <= 0;
      const atRight = el.scrollLeft >= maxScroll - 1;
      const dy = e.deltaY;

      if (dy < 0 && atLeft) return;
      if (dy > 0 && atRight) return;

      e.preventDefault();
      el.scrollLeft = Math.min(maxScroll, Math.max(0, el.scrollLeft + dy));
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("scroll", updateEdges, { passive: true });
    const ro = new ResizeObserver(updateEdges);
    ro.observe(el);

    updateEdges();

    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("scroll", updateEdges);
      ro.disconnect();
    };
  }, [updateEdges]);

  const btnClass =
    "absolute top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-deep-navy/15 bg-warm-white/95 text-deep-navy shadow-sm backdrop-blur-sm transition-colors hover:border-gold/50 hover:text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-warm-white disabled:pointer-events-none disabled:opacity-30";

  return (
    <div className="relative min-w-0 w-full">
      <button
        type="button"
        aria-label="Previous reviews"
        className={cn(btnClass, "left-0 sm:left-1")}
        onClick={scrollPrev}
        disabled={!canPrev}
      >
        <ChevronLeft className="h-5 w-5" aria-hidden />
      </button>
      <button
        type="button"
        aria-label="Next reviews"
        className={cn(btnClass, "right-0 sm:right-1")}
        onClick={scrollNext}
        disabled={!canNext}
      >
        <ChevronRight className="h-5 w-5" aria-hidden />
      </button>
      <div
        ref={ref}
        className={cn(
          "testimonials-scroll flex gap-5 overflow-x-auto overflow-y-hidden snap-x snap-mandatory min-w-0 w-full scroll-smooth pl-11 pr-11 sm:pl-12 sm:pr-12 scroll-pl-11 scroll-pr-11 sm:scroll-pl-12 sm:scroll-pr-12",
          className
        )}
        {...props}
      >
        {children}
      </div>
    </div>
  );
}
